import { supabase } from '../lib/supabase'
import type {
  CreateQualityFlowchartPayload,
  QualityFlowchart,
  QualityFlowchartFilters,
  QualityFlowchartGraph,
  QualityFlowchartVersion,
  UpdateQualityFlowchartPayload,
} from '../types/qualityFlowchart'
import {
  EMPTY_FLOWCHART_GRAPH,
  normalizeFlowchartStatus,
  normalizeGraph,
  sanitizeFlowGraph,
} from '../types/qualityFlowchart'

function mapFlowchartRow(row: QualityFlowchart): QualityFlowchart {
  return {
    ...row,
    status: normalizeFlowchartStatus(row.status),
    flow_json: normalizeGraph(row.flow_json),
  }
}

async function getMultipleUsersInfo(
  userIds: string[]
): Promise<Record<string, { name: string; email: string }>> {
  try {
    const { data, error } = await supabase.rpc('get_multiple_users_info', {
      user_ids: userIds,
    })

    if (error || !data) return {}

    const userMap: Record<string, { name: string; email: string }> = {}
    if (Array.isArray(data)) {
      data.forEach((user: { id: string; name?: string; email?: string }) => {
        userMap[user.id] = {
          name: user.name || 'Usuário',
          email: user.email || '',
        }
      })
    }
    return userMap
  } catch {
    return {}
  }
}

async function enrichWithUserNames(
  rows: QualityFlowchart[]
): Promise<QualityFlowchart[]> {
  const userIds = Array.from(
    new Set(
      rows
        .flatMap((r) => [r.created_by, r.updated_by])
        .filter((id): id is string => Boolean(id))
    )
  )

  if (userIds.length === 0) return rows

  const userMap = await getMultipleUsersInfo(userIds)
  return rows.map((row) => ({
    ...row,
    created_by_name: row.created_by
      ? userMap[row.created_by]?.name || 'Usuário'
      : undefined,
    updated_by_name: row.updated_by
      ? userMap[row.updated_by]?.name || 'Usuário'
      : undefined,
  }))
}

async function createFlowchartVersion(
  flowchartId: string,
  versionNumber: number,
  title: string,
  description: string | null,
  flowJson: QualityFlowchartGraph,
  changeSummary?: string
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const { error } = await supabase.from('process_flowchart_versions').insert({
    flowchart_id: flowchartId,
    version_number: versionNumber,
    title,
    description,
    flow_json: flowJson,
    created_by: user.id,
    change_summary: changeSummary ?? null,
  })

  if (error) {
    throw new Error(`Erro ao criar versão: ${error.message}`)
  }
}

async function createNextVersion(
  flowchartId: string,
  title: string,
  description: string | null,
  flowJson: QualityFlowchartGraph,
  changeSummary: string
): Promise<number> {
  const { data: currentRow } = await supabase
    .from('process_flowcharts')
    .select('current_version')
    .eq('id', flowchartId)
    .single()

  const newVersionNumber = (currentRow?.current_version || 0) + 1

  await createFlowchartVersion(
    flowchartId,
    newVersionNumber,
    title,
    description,
    flowJson,
    changeSummary
  )

  await supabase
    .from('process_flowcharts')
    .update({ current_version: newVersionNumber })
    .eq('id', flowchartId)

  return newVersionNumber
}

export async function listFlowcharts(
  filters: QualityFlowchartFilters = {},
  page: number = 1,
  limit: number = 25
): Promise<{
  items: QualityFlowchart[]
  total: number
  page: number
  limit: number
  totalPages: number
}> {
  const sortBy = filters.sort_by || 'updated_at'
  const ascending = filters.sort_order === 'asc'

  let query = supabase
    .from('process_flowcharts')
    .select('*')
    .is('deleted_at', null)
    .order(sortBy, { ascending })

  let countQuery = supabase
    .from('process_flowcharts')
    .select('*', { count: 'exact', head: true })
    .is('deleted_at', null)

  if (filters.query?.trim()) {
    const q = filters.query.trim()
    const orFilter = `title.ilike.%${q}%,description.ilike.%${q}%`
    query = query.or(orFilter)
    countQuery = countQuery.or(orFilter)
  }

  if (filters.status) {
    query = query.eq('status', filters.status)
    countQuery = countQuery.eq('status', filters.status)
  }

  const offset = (page - 1) * limit
  const { count: totalCount } = await countQuery
  const { data, error } = await query.range(offset, offset + limit - 1)

  if (error) {
    throw new Error(`Erro ao carregar fluxogramas: ${error.message}`)
  }

  const raw = (data || []) as QualityFlowchart[]
  const items = await enrichWithUserNames(raw.map(mapFlowchartRow))
  const total = totalCount || 0

  return {
    items,
    total,
    page,
    limit,
    totalPages: Math.ceil(total / limit) || 0,
  }
}

export async function getFlowchartById(
  id: string
): Promise<QualityFlowchart | null> {
  const { data, error } = await supabase
    .from('process_flowcharts')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw new Error(`Erro ao carregar fluxograma: ${error.message}`)
  }

  const [enriched] = await enrichWithUserNames([
    mapFlowchartRow(data as QualityFlowchart),
  ])
  return enriched
}

export async function getFlowchartVersions(
  flowchartId: string
): Promise<QualityFlowchartVersion[]> {
  const { data, error } = await supabase
    .from('process_flowchart_versions')
    .select('*')
    .eq('flowchart_id', flowchartId)
    .order('version_number', { ascending: false })

  if (error) {
    throw new Error(`Erro ao buscar versões: ${error.message}`)
  }

  const versions = (data || []) as QualityFlowchartVersion[]
  const authorIds = Array.from(
    new Set(versions.map((v) => v.created_by).filter(Boolean))
  ) as string[]

  let userMap: Record<string, { name: string; email: string }> = {}
  if (authorIds.length > 0) {
    userMap = await getMultipleUsersInfo(authorIds)
  }

  return versions.map((v) => ({
    ...v,
    flow_json: normalizeGraph(v.flow_json),
    user_name: v.created_by
      ? userMap[v.created_by]?.name || 'Usuário'
      : 'Usuário',
  }))
}

export async function getFlowchartVersion(
  flowchartId: string,
  versionNumber: number
): Promise<QualityFlowchartVersion | null> {
  const { data, error } = await supabase
    .from('process_flowchart_versions')
    .select('*')
    .eq('flowchart_id', flowchartId)
    .eq('version_number', versionNumber)
    .maybeSingle()

  if (error) {
    throw new Error(`Erro ao carregar versão: ${error.message}`)
  }
  if (!data) return null

  const row = data as QualityFlowchartVersion
  return {
    ...row,
    flow_json: normalizeGraph(row.flow_json),
  }
}

export async function restoreFlowchartVersion(
  flowchartId: string,
  versionNumber: number
): Promise<QualityFlowchart> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const existing = await getFlowchartById(flowchartId)
  if (!existing) {
    throw new Error('Fluxograma não encontrado')
  }
  if (existing.status !== 'published') {
    throw new Error('Somente fluxogramas publicados possuem histórico de versões')
  }

  const version = await getFlowchartVersion(flowchartId, versionNumber)
  if (!version) {
    throw new Error('Versão não encontrada')
  }

  const flowJson = normalizeGraph(version.flow_json)
  const newVersionNumber = await createNextVersion(
    flowchartId,
    version.title,
    version.description,
    flowJson,
    `Restaurado da versão ${versionNumber}`
  )

  const { data, error } = await supabase
    .from('process_flowcharts')
    .update({
      title: version.title,
      description: version.description,
      flow_json: flowJson,
      updated_by: user.id,
      current_version: newVersionNumber,
      status: 'published',
    })
    .eq('id', flowchartId)
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(
      `Erro ao restaurar versão: ${error?.message || 'Erro desconhecido'}`
    )
  }

  return mapFlowchartRow(data as QualityFlowchart)
}

export async function createFlowchart(
  payload: CreateQualityFlowchartPayload
): Promise<QualityFlowchart> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const title = payload.title.trim() || 'Novo fluxograma'
  const description = payload.description?.trim() || null
  const flowJson = sanitizeFlowGraph(
    payload.flow_json ?? { ...EMPTY_FLOWCHART_GRAPH }
  )

  const { data, error } = await supabase
    .from('process_flowcharts')
    .insert({
      title,
      description,
      flow_json: flowJson,
      status: 'draft',
      created_by: user.id,
      updated_by: user.id,
      current_version: 0,
    })
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(
      `Erro ao criar fluxograma: ${error?.message || 'Erro desconhecido'}`
    )
  }

  return mapFlowchartRow(data as QualityFlowchart)
}

export async function updateFlowchart(
  id: string,
  payload: UpdateQualityFlowchartPayload
): Promise<QualityFlowchart> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const { data: existing, error: existingError } = await supabase
    .from('process_flowcharts')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (existingError || !existing) {
    throw new Error(
      `Erro ao atualizar fluxograma: ${existingError?.message || 'Não encontrado'}`
    )
  }

  if ((existing as QualityFlowchart).deleted_at) {
    throw new Error('Fluxograma excluído e não pode ser atualizado')
  }

  const existingMapped = mapFlowchartRow(existing as QualityFlowchart)
  const nextTitle =
    payload.title !== undefined
      ? payload.title.trim() || 'Novo fluxograma'
      : existingMapped.title
  const nextDescription =
    payload.description !== undefined
      ? payload.description?.trim() || null
      : existingMapped.description
  const nextFlowJson =
    payload.flow_json !== undefined
      ? sanitizeFlowGraph(payload.flow_json)
      : existingMapped.flow_json

  const shouldVersion =
    existingMapped.status === 'published' && payload.createVersion === true

  const { data, error } = await supabase
    .from('process_flowcharts')
    .update({
      updated_by: user.id,
      title: nextTitle,
      description: nextDescription,
      flow_json: nextFlowJson,
    })
    .eq('id', id)
    .is('deleted_at', null)
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(
      `Erro ao atualizar fluxograma: ${error?.message || 'Erro desconhecido'}`
    )
  }

  const mapped = mapFlowchartRow(data as QualityFlowchart)
  if (
    payload.flow_json !== undefined &&
    mapped.flow_json.nodes.length !== nextFlowJson.nodes.length
  ) {
    throw new Error(
      `Persistência incompleta do fluxograma (esperados ${nextFlowJson.nodes.length} nós, retornados ${mapped.flow_json.nodes.length})`
    )
  }

  if (shouldVersion) {
    const titleChanged = nextTitle !== existingMapped.title
    await createNextVersion(
      id,
      nextTitle,
      nextDescription,
      nextFlowJson,
      payload.change_summary ||
        (titleChanged ? 'Atualização do título' : 'Atualização do fluxograma')
    )
  }

  const { data: refreshed } = await supabase
    .from('process_flowcharts')
    .select('*')
    .eq('id', id)
    .single()

  return mapFlowchartRow((refreshed || data) as QualityFlowchart)
}

/** Promotes a draft to published and creates the first version snapshot. */
export async function publishFlowchart(
  id: string,
  payload?: {
    title?: string
    description?: string | null
    flow_json?: QualityFlowchartGraph
  }
): Promise<QualityFlowchart> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  const existing = await getFlowchartById(id)
  if (!existing) {
    throw new Error('Fluxograma não encontrado')
  }
  if (existing.status === 'published') {
    return existing
  }

  const nextTitle =
    payload?.title !== undefined
      ? payload.title.trim() || 'Novo fluxograma'
      : existing.title
  const nextDescription =
    payload?.description !== undefined
      ? payload.description?.trim() || null
      : existing.description
  const nextFlowJson =
    payload?.flow_json !== undefined
      ? sanitizeFlowGraph(payload.flow_json)
      : existing.flow_json

  const { data, error } = await supabase
    .from('process_flowcharts')
    .update({
      title: nextTitle,
      description: nextDescription,
      flow_json: nextFlowJson,
      status: 'published',
      updated_by: user.id,
    })
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(
      `Erro ao publicar fluxograma: ${error?.message || 'Erro desconhecido'}`
    )
  }

  await createNextVersion(
    id,
    nextTitle,
    nextDescription,
    nextFlowJson,
    'Publicação'
  )

  const refreshed = await getFlowchartById(id)
  return refreshed ?? mapFlowchartRow(data as QualityFlowchart)
}

export async function deleteFlowchart(id: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Usuário não autenticado')
  }

  // Exclusão lógica — mantém o registro e o histórico de versões no banco
  const { data, error } = await supabase
    .from('process_flowcharts')
    .update({
      deleted_at: new Date().toISOString(),
      deleted_by: user.id,
    })
    .eq('id', id)
    .is('deleted_at', null)
    .select('id')
    .maybeSingle()

  if (error) {
    const hint =
      error.message?.includes('deleted_at') || error.message?.includes('deleted_by')
        ? ' Execute o SQL frontend/sqls/process_04_flowchart_soft_delete.sql no Supabase.'
        : ''
    throw new Error(`Erro ao excluir fluxograma: ${error.message}${hint}`)
  }

  if (!data) {
    throw new Error(
      'Não foi possível excluir o fluxograma (sem permissão, já excluído ou script de exclusão lógica não aplicado). Execute frontend/sqls/process_04_flowchart_soft_delete.sql no Supabase.'
    )
  }
}

export const qualityFlowchartsService = {
  list: listFlowcharts,
  getById: getFlowchartById,
  create: createFlowchart,
  update: updateFlowchart,
  publish: publishFlowchart,
  delete: deleteFlowchart,
  getVersions: getFlowchartVersions,
  getVersion: getFlowchartVersion,
  restoreVersion: restoreFlowchartVersion,
}
